import React, { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogActions, AppBar, Toolbar, IconButton, Typography, Button, Icon, TextField, InputAdornment, Chip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LibraryIcon from '@mui/icons-material/Book';
import useStyles from './TaskCreate.styles';
import { get, set, size, last, sortBy, first } from 'global/lib/lodash';
import Form from 'global/components/form';
import Styles, { useScreen, Loader } from 'global/styles';
import Alert from '@mui/material/Alert';
import clsx from 'clsx';
import TaskTag from './TaskTag';
import ScoreIcon from 'global/components/ScoreIcon';
import sendData from 'redux/actions/sendData';
import { useDispatch } from 'react-redux';
import { addNewTask } from 'redux/actions/view';
import qs from 'qs';
import useRetrieveData from 'global/hooks/useRetrieveData';
import { useAccessEdition } from 'global/utils/edition';
import { Link } from 'react-router-dom';
import { UploadFiles } from 'redux/actions/imageUploader';
import { getDisplayValue } from 'global/utils/getDisplayScore';
import { useUser } from 'global/hooks/useUser';
import { LI } from '../../report/Highlights';
import moment from 'moment';
import { fixWeirdDateIssue } from '../../../global/components/form';
import { taskOptions, SETTINGS } from '../../admin/account-settings';
import { getAccountSetting } from 'redux/actions/userLogin';
import { Text } from '../../../global/components/text';
import { getCustomFieldInputs, getCloseoutFromTaskType, getCloseoutDefaults, getStandardFieldRequirements, UploadCustomFieldMedia } from '../task-type-utils';
import { splitMediaByType } from 'global/utils/mediaUtils';

export const RenderTag = ({ tag, showLink }) => {
  const classes = useStyles();
  const hasReportSection = get(tag, 'reportSection');
  const checkpointId = get(tag, 'checkpointId');
  const checklistId = get(tag, 'checklistId');

  const params = [
    { url: `/edition-documents` },
    { build: ['editionDocuments'], params: { filter: { documentable_type: 'checklist', documentable_id: checklistId } }, showAlert: false, transformData: (d) => last(d) },
  ];

  const { data } = useRetrieveData(...params);
  const editionId = get(data, 'editionId');
  const bool = useAccessEdition(editionId);

  const scscore = useRetrieveData(
    { url: `/scorecard-scores/${get(tag, 'scorecardScoreId')}` },
    {
      build: ['scorecardScores'],
      waitFor: !!get(tag, 'scorecardScoreId') ? undefined : null,
      transformData: d => {
        return first(d);
      }
    }
  );

  const getLink = (() => {
    if (bool) {
      return `/library/${editionId}/document/${get(data, 'classificationId')}${checkpointId ? `?hl=${checkpointId}` : ''}`;
    }
    return '';
  })();

  const superSet = get(data, 'superset');

  if (hasReportSection) {
    return (
      <>
        <LI li={hasReportSection} collapseContentOnly />
      </>
    );
  }

  const score = get(tag, 'score');
  const displayValue = getDisplayValue({ value: score }, get(tag, 'scorecardConfig', null));
  const sibscore = get(scscore, 'data.statusSiblings.0');

  // checkpointId determines whether the score is a checkpoint score or a general observation (a checklist score)
  return (
    <>
      {superSet && <div className={classes.superText}>{get(superSet, 'name')}</div>}
      <div className={classes.tagContainer}>
        <p className={classes.tagText}>
          {!!checkpointId && <span className={classes.tagOption}>{displayValue}</span>}
          <span className={classes.tagBox}>
            <ScoreIcon center score={!!checkpointId ? score : undefined} />
          </span>
          <span className={classes.tagName}>{get(tag, 'name', '')}</span>
          <span className={classes.tagDescription}>{!!checkpointId ? get(tag, 'criteria') || get(tag, 'description') : `General Observation: ${get(tag, 'pssvChecklist.name')}`}</span>
        </p>
      </div>

      {!!sibscore && <div>
        <div><Text bold>TARGET</Text></div>
        <div className={classes.tagContainer}>
          <div className={classes.tagText}>
            {!!checkpointId && <span className={classes.tagOption}>{getDisplayValue({ value: sibscore.score }, get(tag, 'scorecardConfig', null))}</span>}
            <span className={classes.tagBox}>
              <ScoreIcon center score={!!checkpointId ? sibscore.score : undefined} />
            </span>
            <span className={classes.tagName}>{get(tag, 'name', '')}</span>
            <span className={classes.tagDescription}>{get(sibscore, 'criteria') || get(sibscore, 'description')}</span>
          </div>
        </div>
      </div>}

      {showLink && getLink && (
        <div className={classes.tagLink}>
          <Typography className={classes.tagReference} variant="body2" component={Link} to={getLink}>
            <LibraryIcon />
            Show Reference Document
          </Typography>
        </div>
      )}
    </>
  );
};

// eslint-disable-next-line import/no-anonymous-default-export
export default (props) => {
  const [formLoading, setFormLoading] = useState(false);
  const [error, setError] = useState();
  const [tag, setTag] = useState();
  const [formState, setFormState] = useState({});
  const [tagModal, setTagModal] = useState(false);
  const [tagName, setTagName] = useState();
  const { me } = useUser();
  const dispatch = useDispatch();
  const formRef = useRef(null);

  const classes = useStyles();
  const styles = Styles();
  const { isMobileXS } = useScreen();

  const usersList = useRetrieveData(
    { url: '/users' },
    {
      waitFor: get(props, 'realmItem.realm.id', null),
      build: ['users'],
      params: { filter: { content_realm_id: get(props, 'realmItem.realm.id') } },
      useCache: { reload: 1 },
    }
  );

  const reset = (formState = {}) => {
    setFormState(formState);
    setTagModal(false);
    setError();
    setTag();
    setFormLoading(false);
  };

  const handleClose = async (tsk) => {
    await props.onClose(tsk);
    reset();
  };

  const formatData = async (values) => {
    const relationships = {};

    let tresult;
    if (tag) {
      const selectedOption = get(tag, 'pssvCheckpoint.selectedOption');
      const cpo = get(tag, `pssvCheckpoint.checkpointOptions.${get(selectedOption, 'index')}`);
      tresult = await sendData({
        url: '/scorecard-scores',
        method: 'post',
        data: {
          data: {
            type: 'scorecard-scores',
            attributes: {
              scorecard_id: get(tag, 'scorecardId'),
              checklist_id: get(tag, 'pssvChecklist.id'),
              checkpoint_id: get(tag, 'pssvCheckpoint.id'),
              checkpoint_option_id: get(cpo, 'id'),
              criteria: [get(tag, 'pssvCheckpoint.description'), get(cpo, 'name')].filter(Boolean).join(' '),
              score: get(cpo, 'value', 0),
            },
          },
        },
      });
    }

    // Split combined media into images and files
    const { images: splitImages, documents: splitDocs } = splitMediaByType(get(formState, 'media', []));

    const attributes = {
      name: get(values, 'name'),
      description: get(values, 'description'),
      status: 0,
      realm_id: get(props, 'realmItem.realm.id'),
      scorecard_score_id: get(tresult, 'data.data.id'),
      checkpoint_id: get(tag, 'pssvCheckpoint.id'),
      due_date: get(formState, 'dueDate'),
      images: splitImages.map((p) => ({ ...p, name: p.path || p.name, caption: get(p, 'caption', '') })),
      meta: {
        ...get(formState, 'meta', {}),
      },
      ...(props.taskType ? { task_type_id: String(props.taskType.id) } : {}),
    };

    const assignees = get(formState, 'assignedTo');
    set(relationships, 'assignees', {
      data: size(assignees) ? assignees.map((e) => ({ type: 'users', role: 'assignee', id: e.id })) : [{ type: 'users', role: 'assignee', id: me.id }],
    });

    set(relationships, 'authors', {
      data: [{ type: 'users', role: 'author', id: get(me, 'id', '').toString() }],
    });

    const data = {
      data: {
        type: 'tasks',
        attributes,
        relationships,
      },
    };

    const tradeTypes = get(formState, 'tradeTypes');
    if (size(tradeTypes)) {
      set(relationships, 'tradeTypes', {
        data: (tradeTypes || []).map((t, i) => ({ type: 'trade-types', id: t.id, pivot: { order: `${i}` } })),
      });
    }

    if (size(attributes.images)) {
      await UploadFiles(data);
    }

    const documents = splitDocs.map((f) => ({ ...f, name: get(f, 'file.name', f.name) }));
    if (size(documents)) {
      set(data, 'data.attributes.files', documents);
      await UploadFiles(data, { attributeType: 'files', isImage: false, collection_name: 'task-files' });
    }

    // Process custom field media: patch captions, clean values, add to relationships
    if (props.taskType) {
      await UploadCustomFieldMedia(data, {
        rawCfv: get(formState, 'custom_field_values') || {},
        taskTypeId: String(props.taskType.id),
      });
    }

    return data;
  };

  const formSubmit = async (addAnother = false) => {
    try {
      setFormLoading(true);
      const data = await formatData(formState);
      const createdTask = await sendData({
        url: '/tasks',
        method: 'post',
        data,
      });
      const tsk = get(createdTask, 'data.data');
      const include =
        'images,files,assignees,viewers,authors,comments,comments.author,comments.images,comments.files,realm,scorecardScore,scorecardScore.checklist,scorecardScore.checkpoint,scorecardScore.checkpoint.checkpointOptions,tradeTypes';
      const fullTask = await sendData(
        {
          url: `/tasks/${tsk.id}?${qs.stringify({ include })}`,
          method: 'get',
        },
        { normalize: { build: ['tasks'] } }
      );
      dispatch(addNewTask(get(fullTask, '0')));
      if (addAnother) {
        const assignedTo = get(formState, 'assignedTo');
        reset({ assignedTo });
      } else {
        await handleClose(tsk);
      }
      setFormLoading(false);
    } catch (err) {
      setFormLoading(false);
      setError(`${err.message}. Please try again.`);
    }
  };

  const formUpdate = (vals) => {
    for (let k in vals) {
      set(formState, k, vals[k]);
    }
    setFormState({ ...formState });
    if (formRef.current) {
      const result = formRef.current.canContinue(true);
      setIsFormValid(result.passes);
    }
  };

  const clearTag = () => {
    setTag();
    setTagName();
    if (tagName === get(formState, 'name')) {
      formRef.current.updateState('name', '');
    }
  };

  const onTagSelected = (tags) => {
    setTag(tags);
    if (!get(formState, 'name')) {
      const clname = get(tags, 'pssvChecklist.name');
      const cpname = get(tags, 'pssvCheckpoint.name');
      const name = clname + (cpname ? `: ${cpname}` : '');
      setTagName(name);
      formRef.current.updateState('name', name);
    }
  };

  const [customFieldErrors, setCustomFieldErrors] = useState([]);
  const [isFormValid, setIsFormValid] = useState(false);

  const handleSaveClick = (addAnother) => {
    const result = formRef.current.validateRequired();
    if (result.passes) {
      setCustomFieldErrors([]);
      formSubmit(addAnother);
    }
  };

  return (
    <Dialog className={classes.dialog} fullScreen disableScrollLock disableEscapeKeyDown open={props.open} onClose={() => handleClose()}>
      <AppBar color="secondary" className={classes.appBar}>
        <Toolbar className={classes.toolbar}>
          <IconButton edge="start" color="inherit" onClick={() => handleClose()} aria-label="Close" size="large">
            <CloseIcon />
          </IconButton>

          <div className={classes.barTitle}>
            <Typography variant="h5" className={classes.title}>
              {props.taskType ? `Create Task - ${props.taskType.name}` : 'Create Task'}
            </Typography>
          </div>

          <Typography variant="h6" className={classes.status}>
            {get(props, 'realmItem.realmName', '')}
            <Icon className={classes.statusIcon}>place</Icon>
          </Typography>
        </Toolbar>
      </AppBar>

      {!formLoading && (
        <DialogContent className={classes.container}>
          {error && (
            <Alert
              severity="error"
              action={
                <IconButton
                  aria-label="close"
                  color="inherit"
                  size="small"
                  onClick={() => {
                    setError();
                  }}
                >
                  <CloseIcon fontSize="inherit" />
                </IconButton>
              }
            >
              {error}
            </Alert>
          )}

          <div className={classes.buttonContainer}>
            <Button
              variant="outlined"
              startIcon={<Icon>{tag ? 'close' : 'label'}</Icon>}
              onClick={tag ? () => clearTag() : () => setTagModal(true)}
              className={clsx(classes.tagButton, tag && classes.tagRemove)}
            >
              {tag ? 'REMOVE LINK' : 'ADD LINK'}
            </Button>

            {tag && <RenderTag tag={tag} />}
          </div>

          <Form
            loading={formLoading}
            showAllScreens={true}
            formStyle={classes.formStyle}
            onSubmit={formSubmit}
            onFormState={formUpdate}
            ref={formRef}
            useHeight={false}
            screens={[
              {
                title: '',
                titleStyle: classes.titleStyle,
                screenContainerStyle: isMobileXS ? classes.screenContainerMobile : classes.screenContainer,
                screenContainer: {
                  props: {
                    className: classes.screenWidth,
                  },
                },
                inputs: (() => {
                  // Determine which standard fields are required from task type
                  const stdReqs = props.taskType ? getStandardFieldRequirements(props.taskType) : [];
                  const isFieldRequired = (fid) => stdReqs.find((f) => f.id === fid)?.is_required;
                  const isFieldEnabled = (fid) => { const f = stdReqs.find((s) => s.id === fid); return f ? f.is_enabled !== false : true; };

                  // Default field input definitions keyed by their field_order IDs
                  const defaultFieldMap = {
                    f_title: {
                      id: 'name',
                      type: 'TextField',
                      required: isFieldRequired('f_title'),
                      defaultValue: () => get(formState, 'name'),
                      props: { placeholder: 'Enter Title' },
                    },
                    f_description: {
                      id: 'description',
                      type: 'TextField',
                      required: isFieldRequired('f_description'),
                      defaultValue: () => get(formState, 'description'),
                      props: { placeholder: props.taskType ? `Describe your ${props.taskType.name}` : 'Describe your task', maxRows: 5, multiline: true },
                    },
                    f_assignee: {
                      id: 'assignedTo',
                      type: 'UserSelect',
                      required: isFieldRequired('f_assignee'),
                      defaultValue: () => get(formState, 'assignedTo', []),
                      data: () => usersList.data,
                      props: { label: 'Assigned To' },
                    },
                    f_due_date: {
                      id: 'dueDate',
                      type: 'KeyboardDatePicker',
                      required: isFieldRequired('f_due_date'),
                      defaultValue: () => (get(formState, 'dueDate') ? moment(get(formState, 'dueDate')) : undefined),
                      props: { label: 'Due Date', convert: (d) => fixWeirdDateIssue(d, !formState.dueDate) },
                    },
                    f_trade: {
                      id: 'tradeTypes',
                      type: 'TradeSelect',
                      required: isFieldRequired('f_trade'),
                      defaultValue: () => get(formState, 'tradeTypes', []),
                      props: { label: 'Trade Types', checklist: get(tag, 'pssvChecklist') },
                    },
                    f_media: {
                      id: 'media',
                      type: 'DropArea',
                      required: isFieldRequired('f_media'),
                      defaultValue: () => sortBy(get(formState, 'media', []), 'orderColumn'),
                      props: {
                        label: 'Media', sortable: true, assessment: props.assessment,
                        uploadAllNew: true, shouldUpdateOnCaptionChange: true,
                        collection: 'task-images', notAllowed: ['tag'],
                        bothMode: true, accept: null,
                      },
                    },
                  };

                  const metaInput = {
                    id: 'meta',
                    type: 'Checkbox',
                    options: props.taskType ? getCloseoutFromTaskType(props.taskType) : taskOptions(),
                    defaultValue: () => props.taskType ? getCloseoutDefaults(props.taskType) : getAccountSetting(SETTINGS.taskDefault),
                    props: { label: 'Requirements' },
                  };

                  const fieldOrder = get(props, 'taskType.field_order');
                  if (fieldOrder && Array.isArray(fieldOrder)) {
                    // Build custom field map keyed by cf_ id
                    const customInputMap = {};
                    if (props.taskType) {
                      getCustomFieldInputs(props.taskType, formState, { errors: customFieldErrors }).forEach((input) => {
                        // input.id is 'custom_field_values.cf_xxx' - extract the cf_ id
                        const cfId = input.id.replace('custom_field_values.', '');
                        customInputMap[cfId] = input;
                      });
                    }

                    const ordered = [];
                    fieldOrder.forEach((fid) => {
                      if (fid === 'f_documents') return; // merged into f_media
                      if (defaultFieldMap[fid] && isFieldEnabled(fid)) {
                        ordered.push(defaultFieldMap[fid]);
                      } else if (customInputMap[fid]) {
                        ordered.push(customInputMap[fid]);
                      }
                    });

                    // Append any custom fields not in field_order (newly added)
                    Object.keys(customInputMap).forEach((cfId) => {
                      if (!fieldOrder.includes(cfId)) {
                        ordered.push(customInputMap[cfId]);
                      }
                    });

                    // Requirements always last
                    ordered.push(metaInput);
                    return ordered;
                  }

                  // Fallback: no field_order, use original hardcoded order
                  return [
                    defaultFieldMap.f_title,
                    ...(isFieldEnabled('f_description') ? [defaultFieldMap.f_description] : []),
                    ...(isFieldEnabled('f_assignee') ? [defaultFieldMap.f_assignee] : []),
                    ...(isFieldEnabled('f_due_date') ? [defaultFieldMap.f_due_date] : []),
                    ...(isFieldEnabled('f_trade') ? [defaultFieldMap.f_trade] : []),
                    ...(isFieldEnabled('f_media') ? [defaultFieldMap.f_media] : []),
                    ...(props.taskType ? getCustomFieldInputs(props.taskType, formState, { errors: customFieldErrors }) : []),
                    metaInput,
                  ];
                })(),
              },
            ]}
          />
        </DialogContent>
      )}

      {!formLoading && (
        <DialogActions className={classes.submitButtonContainer}>
          <Button
            startIcon={
              <div className={classes.doubleIcon}>
                <Icon>check_plus</Icon>
                <Icon className={classes.plusIcon}>add</Icon>
              </div>
            }
            variant={'contained'}
            color="secondary"
            onClick={() => handleSaveClick(true)}
            classes={{ root: clsx(classes.button, !isFormValid && classes.buttonDisabledLook) }}
          >
            <span className={classes.submitBtnText}>Save & Add</span>
          </Button>

          <Button
            startIcon={<Icon style={{ fontSize: 30 }}>check</Icon>}
            variant={'contained'}
            color="secondary"
            onClick={() => handleSaveClick()}
            classes={{ root: clsx(classes.button, !isFormValid && classes.buttonDisabledLook) }}
          >
            <span className={classes.submitBtnText}>Save & Close</span>
          </Button>
        </DialogActions>
      )}

      {formLoading && (
        <div className={clsx(classes.container, styles.centerColumn)}>
          <Loader size={40} />
        </div>
      )}

      <TaskTag open={tagModal} onTagSelected={onTagSelected} onClose={() => setTagModal(false)} />
    </Dialog>
  );
};
